import { useEffect, useMemo } from 'react';
import { AccommodationsConsoleState, AccommodationListingDTO, StandardHotelPricingValues } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useBackgroundPricingRefresh } from './useBackgroundPricingRefresh';
import { Maybe } from '@graphql/generated';

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  skipCouplePicks?: boolean;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
  shouldPrioritizeParams?: boolean;
  eventHandle?: Maybe<string>;
}>;

const mergeBackgroundPricing = (listings: AccommodationListingDTO[], pricingMap: Map<string, StandardHotelPricingValues | null>): AccommodationListingDTO[] => {
  if (pricingMap.size === 0) return listings;

  return listings.map(listing => {
    if (listing.type !== 'standardHotel' || !listing.joyPlaceId) return listing;

    if (!pricingMap.has(listing.joyPlaceId)) return listing;

    const freshPricing = pricingMap.get(listing.joyPlaceId);

    return {
      ...listing,
      pricingInfo: {
        isLoading: false,
        values: freshPricing ?? null,
      },
    };
  });
};

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, variant, skipCouplePicks = false, onError, onSuccess, isInitialLoad, shouldPrioritizeParams, eventHandle } = args;

  const isViewingCouplesPicks = variant === 'couple' && !skipCouplePicks;

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({
    state,
    skip: skipCouplePicks,
  });
  const { isFetchingListings, listings, cachedListings, error, confirmedSearchPayload } = useRegionalHotels({
    state,
    isInitialLoad,
    shouldPrioritizeParams,
  });

  const baseConsolidatedListings = useMemo(() => {
    if (skipCouplePicks) {
      // When skipping couple picks, return only regional hotels
      return cachedListings;
    }

    const eventAccommodationsFromCatalogByJoyPlaceIdSet = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as Array<string>);

    return [
      ...eventAccommodations.map(accommodation => ({
        ...accommodation,
        searchScore: cachedListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId)?.searchScore,
      })),
      // Remove listings that are already chosen by the event
      ...cachedListings.filter(listing => !!listing.joyPlaceId && !eventAccommodationsFromCatalogByJoyPlaceIdSet.has(listing.joyPlaceId)),
    ];
  }, [eventAccommodations, cachedListings, skipCouplePicks]);

  const { pricingMap: backgroundPricingMap, isLoading: isBackgroundPricingLoading } = useBackgroundPricingRefresh({
    eventHandle,
    isInitialLoad,
    isFetchingListings,
    confirmedSearchPayload,
  });

  const consolidatedListings = useMemo(() => mergeBackgroundPricing(baseConsolidatedListings, backgroundPricingMap), [baseConsolidatedListings, backgroundPricingMap]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    } else if (!isFetchingListings && listings.length > 0) {
      onSuccess?.();
    }
  }, [error, isFetchingListings, listings.length, onError, onSuccess]);

  return {
    /**
     * Can be used to retain maps points while loading new data
     */
    cachedListings,
    listings: consolidatedListings,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    isFetchingListingPrices: false,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null, //error
    isBackgroundPricingLoading,
  };
};
